import { useEffect, useState } from 'react';
import notificationHub from '../services/notificationHub';

/**
 * Used on the payment callback/waiting page
 * Connects as a guest and waits for PaymentConfirmed event
 * for this specific reference code
 */
export const usePaymentConfirmation = (referenceCode, onConfirmed) => {
  const [status, setStatus] = useState('waiting'); // waiting | confirmed | failed
  const [timeoutReached, setTimeoutReached] = useState(false);

  useEffect(() => {
    if (!referenceCode) return;

    let timeout;

    const setup = async () => {
      try {
        // connect as guest (no auth needed for client pages)
        await notificationHub.connectAsGuest();

        const handleConfirmed = (data) => {
          const ref = data.referenceCode || data.ReferenceCode;
          if (ref === referenceCode) {
            setStatus('confirmed');
            clearTimeout(timeout);
            notificationHub.leavePaymentGroup(referenceCode);
            onConfirmed?.(data);
          }
        };

        notificationHub.on('PaymentConfirmed', handleConfirmed);

        // Register the listener before joining so a fast confirmation cannot be missed.
        await notificationHub.joinPaymentGroup(referenceCode);

        // fallback timeout — mobile money can take a few minutes
        // if no confirmation after 5 mins, prompt user to check manually
        timeout = setTimeout(() => {
          setTimeoutReached(true);
        }, 5 * 60 * 1000);

        return () => {
          notificationHub.off('PaymentConfirmed', handleConfirmed);
          notificationHub.leavePaymentGroup(referenceCode);
          clearTimeout(timeout);
        };
      } catch (error) {
        console.error('Payment confirmation connection failed:', error);
        setStatus('failed');
      }
    };

    const cleanup = setup();
    return () => {
      cleanup.then(fn => fn?.());
    };
  }, [referenceCode]);

  return { status, timeoutReached };
};
